// D-3: reminders are DEVICE-LOCAL. Product decision: no backend, no auth,
// no notification engine of our own.
//
// "Remind me" therefore does exactly two things:
//   1. hands the upcoming stream to the viewer's own calendar app, which is
//      what actually notifies them — via the add-to-calendar-button package
//      the app already ships (see components/AddToCalendarButton.js);
//   2. persists the toggled id in localStorage so "Reminder set" survives a
//      refresh on this device.
//
// Toggling OFF only clears our local state. A calendar entry the viewer
// already accepted lives in THEIR calendar and we have no way to reach in
// and remove it — so we do not pretend to.
//
// ponytail: no new component, no new dependency, no server. A Set in state
// plus one localStorage key is the whole feature; UpcomingCard's prop-driven
// interface (`reminded` + `onToggleReminder`) is untouched.
import { useState } from 'react';
import posthog from 'posthog-js';
import { atcb_action } from 'add-to-calendar-button';
// The picker's stylesheet — without it the modal renders unstyled with
// full-size SVG icons. Previously loaded only by the unrouted legacy
// Broadcast.js, so the browse screen never got it.
import 'add-to-calendar-button/assets/css/atcb.css';

export const STORAGE_KEY = 'streampark.reminders';

// Stream sources rarely publish an end time; an hour is long enough to be a
// useful calendar block and short enough not to swallow the viewer's day.
const DEFAULT_DURATION_MS = 60 * 60 * 1000;

const pad = (n) => String(n).padStart(2, '0');
/** add-to-calendar-button wants wall-clock date + time plus a timeZone. */
const localDate = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const localTime = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

/**
 * localStorage throws (not returns null) in some privacy modes, and the
 * stored value can be anything a previous version or another tab left there.
 * Both failures degrade to "no reminders yet", never to a broken screen.
 * @returns {Set<number>}
 */
export function readStoredReminders() {
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    return new Set(Array.isArray(stored) ? stored : []);
  } catch (e) {
    return new Set();
  }
}

function writeStoredReminders(reminders) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...reminders]));
  } catch (e) {
    // storage blocked or full: state stays session-only rather than crashing
  }
}

/**
 * Opens the viewer's calendar picker for one upcoming stream. Times are the
 * viewer's own — the source's timezone label never reaches here (A-2).
 * @param {import('./stream').Stream} stream
 */
export function addStreamToCalendar(stream) {
  if (!stream || !stream.startsAt) return; // nothing to put on a calendar
  const start = new Date(stream.startsAt);
  const end = new Date(start.getTime() + DEFAULT_DURATION_MS);
  try {
    atcb_action({
      name: stream.title,
      description: `Watch on Stream Park: ${window.location.origin}/#/stream/${stream.id}`,
      startDate: localDate(start),
      startTime: localTime(start),
      endDate: localDate(end),
      endTime: localTime(end),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      options: ['Apple', 'Google', 'iCal', 'Microsoft365', 'Outlook.com', 'Yahoo'],
      iCalFileName: 'stream-park-reminder',
    });
  } catch (e) {
    // the picker blowing up must not strand the toggle mid-flip
  }
}

/**
 * @param {import('./stream').Stream[]} streams  the current browse list —
 *   toggling on looks the stream up here to build the calendar event
 * @returns {[Set<number>, (streamId: number) => void]}
 */
export default function useReminders(streams) {
  // lazy initializer: hydrate once, on mount, before the first paint
  const [reminders, setReminders] = useState(readStoredReminders);

  const toggleReminder = (streamId) => {
    const next = new Set(reminders);
    const wasSet = next.has(streamId);
    if (wasSet) {
      next.delete(streamId); // local-only; the calendar entry stays put
    } else {
      next.add(streamId);
      addStreamToCalendar(streams.find((s) => s.id === streamId));
    }
    posthog.capture('reminder_toggled', {
      stream_id: streamId,
      action: wasSet ? 'removed' : 'set',
    });
    writeStoredReminders(next);
    setReminders(next);
  };

  return [reminders, toggleReminder];
}
